import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatFecha } from '../state/format';
import { clienteService } from '../../../../clientes/services/clienteService';
import { BuscadorClientes, OpcionCliente } from './BuscadorClientes';

interface ClienteResumenDrawer {
  id: string;
  razonSocial: string;
}

interface ClientBlockProps {
  cliente: ClienteResumenDrawer | null;
  creadoEn: string;
  onAssign: (clienteId: string) => Promise<void>;
}

function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}

// SPEC-103 — bloque de cliente del drawer: solo lectura, salvo la asociación inicial
// cuando la bolsa no tiene cliente (RN-002, UC-002).
export const ClientBlock: React.FC<ClientBlockProps> = ({ cliente, creadoEn, onAssign }) => {
  const [email, setEmail] = useState<string | null>(null);
  const [mostrarSelector, setMostrarSelector] = useState(false);
  const [asignando, setAsignando] = useState(false);

  useEffect(() => {
    if (!cliente) {
      setEmail(null);
      return;
    }
    let cancelado = false;
    clienteService
      .obtener(cliente.id)
      .then((detalle) => {
        if (cancelado) return;
        const principal = detalle.contactos.find((c) => c.esPrincipal) ?? detalle.contactos[0];
        setEmail(principal?.correoElectronico ?? null);
      })
      .catch(() => {
        // Sin bloquear el render del bloque — el email queda vacío (patrón de SendQuoteModal)
      });
    return () => {
      cancelado = true;
    };
  }, [cliente]);

  const handleSeleccionar = async (seleccionado: OpcionCliente | null) => {
    if (!seleccionado) return;
    setAsignando(true);
    try {
      await onAssign(seleccionado.id);
      setMostrarSelector(false);
    } finally {
      setAsignando(false);
    }
  };

  if (!cliente) {
    return (
      <Box
        data-testid="client-block"
        sx={{
          marginTop: '20px',
          backgroundColor: TOKENS.wash,
          borderRadius: '14px',
          padding: '14px 16px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              backgroundColor: TOKENS.hair,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 700, color: TOKENS.muted }}>?</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 600, color: TOKENS.ink }}>
              Sin cliente asociado
            </Typography>
            {!mostrarSelector && (
              <Typography
                data-testid="client-block-asociar"
                onClick={() => setMostrarSelector(true)}
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: fs(12.5),
                  fontWeight: 600,
                  color: TOKENS.purple,
                  cursor: 'pointer',
                  marginTop: '2px',
                  display: 'inline-block',
                }}
              >
                Asociar cliente
              </Typography>
            )}
          </Box>
        </Box>

        {mostrarSelector && (
          <BuscadorClientes
            testId="client-block-selector"
            onSeleccionar={handleSeleccionar}
            deshabilitado={asignando}
            ocupado={asignando}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      data-testid="client-block"
      sx={{
        marginTop: '20px',
        backgroundColor: TOKENS.wash,
        borderRadius: '14px',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          backgroundColor: 'rgba(147,64,143,.14)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13), fontWeight: 700, color: TOKENS.purple }}>
          {iniciales(cliente.razonSocial)}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(13.5), fontWeight: 600, color: TOKENS.ink }}>
          {cliente.razonSocial}
        </Typography>
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11.5), color: TOKENS.muted }}>
          {email ?? 'Sin email registrado'} · elaborada el {formatFecha(creadoEn)}
        </Typography>
      </Box>
    </Box>
  );
};
